import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Field } from '../../../../types/fields';
import { FieldTypeIconComponent } from '../../../../components/field-type-icon.component';
import { DefaultValueInputComponent } from '../../../../components/default-value-input/default-value-input.component';
import { ObjectFieldEditorComponent } from './object-field-editor.component';
import { DocumentTemplateField } from '../../../../types/document-template';

@Component({
  selector: 'app-template-field-editor',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, DragDropModule, MatIconModule, MatTooltipModule, FieldTypeIconComponent, DefaultValueInputComponent, ObjectFieldEditorComponent],
  template: `
    <div animate.enter="field-appear"
         [formGroup]="fieldForm"
         [id]="'field-editor-' + index"
         class="rounded-lg border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 shadow-sm hover:border-blue-300 dark:hover:border-blue-500 transition-all duration-200 overflow-hidden">

      <!-- Header Section - Always Visible -->
      <div class="bg-zinc-50/50 dark:bg-zinc-800/50 px-4 py-3 border-b border-zinc-100 dark:border-zinc-700 flex items-start justify-between gap-3 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
           role="button"
           tabindex="0"
           [attr.aria-expanded]="isExpanded"
           [attr.aria-label]="'Toggle details for field ' + fieldDefinition.name"
           (click)="onToggleExpansion.emit()"
           (keydown.enter)="onToggleExpansion.emit()"
           (keydown.space)="$event.preventDefault(); onToggleExpansion.emit()">
        <div class="flex items-center gap-2 flex-1 min-w-0">
          <mat-icon cdkDragHandle
               class="!text-base text-zinc-300 dark:text-zinc-600 cursor-grab hover:text-zinc-500 dark:hover:text-zinc-400 flex-shrink-0"
               (click)="$event.stopPropagation()">drag_indicator</mat-icon>
          @if (fieldDefinition) {
            <app-field-type-icon [type]="fieldDefinition.type" ></app-field-type-icon>
          }
          <div class="min-w-0">
            <div class="flex items-center gap-1">
              <h4 class="text-sm font-semibold text-zinc-900 dark:text-zinc-100 truncate" [title]="fieldForm.get('name')?.value">
                {{ fieldDefinition.name }}
              </h4>
              @if (fieldDefinition.required) {
                <span class="inline-flex items-center rounded-md bg-red-50 dark:bg-red-900/30 px-1.5 py-0.5 text-[10px] font-medium text-red-700 dark:text-red-300 ring-1 ring-inset ring-red-600/10 dark:ring-red-400/20">Required</span>
              }
            </div>
            @if (!isExpanded && fieldForm.get('exampleValue')?.value) {
              <p class="text-xs text-zinc-500 dark:text-zinc-400 truncate">{{ getPreviewText() }}</p>
            }
          </div>
        </div>
        <div class="flex items-center gap-1 text-zinc-400 dark:text-zinc-500 flex-shrink-0">
          <button type="button"
             matTooltip="Edit field definition"
             aria-label="Edit field definition"
             class="inline-flex items-center justify-center rounded-md p-1 text-zinc-400 dark:text-zinc-500 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 cursor-pointer transition-colors"
             (click)="onEditFieldClick($event)">
            <mat-icon class="!text-base">edit</mat-icon>
          </button>
          <mat-icon class="!text-base transition-transform duration-200" [class.rotate-180]="isExpanded">expand_more</mat-icon>
        </div>
      </div>

      <!-- Expanded Details Section -->
      @if (isExpanded) {
        <!-- Field Info from Schema -->
        @if (fieldDefinition) {
          <div class="px-4 py-3 bg-blue-50/50 dark:bg-blue-900/20 border-b border-blue-100 dark:border-blue-800 text-xs space-y-2">
            @if (fieldDefinition.description) {
              <div>
                <span class="block text-[10px] uppercase font-semibold text-blue-400 dark:text-blue-500 mb-0.5">Field Description</span>
                <p class="text-blue-700 dark:text-blue-300 leading-relaxed">{{ fieldDefinition.description }}</p>
              </div>
            }
            @if (fieldDefinition.enumValues && fieldDefinition.enumValues.length > 0) {
              <div>
                <span class="block text-[10px] uppercase font-semibold text-blue-400 dark:text-blue-500 mb-0.5">Allowed Values</span>
                <div class="flex flex-wrap gap-1">
                  @for (v of fieldDefinition.enumValues; track v) {
                    <span class="inline-flex items-center rounded-md bg-blue-100 dark:bg-blue-900/30 px-1.5 py-0.5 text-[10px] font-medium text-blue-700 dark:text-blue-300 ring-1 ring-inset ring-blue-600/10 dark:ring-blue-400/20">
                      {{ v }}
                    </span>
                  }
                </div>
              </div>
            }
          </div>
        }

        <!-- Input Section -->
        <div class="p-4 space-y-4">
          <div class="space-y-1.5">
            <label [for]="'field-example-' + index" class="text-xs font-medium text-zinc-700 dark:text-zinc-300">Example Value</label>
            @if (fieldDefinition.type === 'object' || fieldDefinition.type === 'object-list') {
              <app-object-field-editor
                [fieldDefinition]="fieldDefinition"
                [value]="fieldForm.get('exampleValue')?.value"
                (valueChange)="fieldForm.get('exampleValue')?.setValue($event)"
              />
            } @else {
              <app-default-value-input
                [id]="'field-example-' + index"
                formControlName="exampleValue"
                [fieldType]="fieldDefinition.type"
                [isError]="fieldDefinition.required && fieldForm.get('exampleValue')?.dirty && !fieldForm.get('exampleValue')?.value"
              ></app-default-value-input>
            }
          </div>

          <div class="space-y-1.5">
            <label [for]="'field-description-' + index" class="text-xs font-medium text-zinc-700 dark:text-zinc-300">Description (Optional)</label>
            <p class="text-[10px] text-zinc-500 dark:text-zinc-400">Template-specific instructions to guide the AI during processing.</p>
            <textarea
              data-testid="template-field-description-input"
              [id]="'field-description-' + index"
              formControlName="description"
              rows="2"
              placeholder="Instructions for this field in this template..."
              class="block w-full rounded-lg border border-zinc-200 dark:border-zinc-600 bg-white dark:bg-zinc-700 px-3 py-2 text-sm text-zinc-900 dark:text-zinc-100 shadow-sm placeholder:text-zinc-400 dark:placeholder:text-zinc-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all hover:bg-zinc-50 dark:hover:bg-zinc-600 resize-y min-h-[48px]"
            ></textarea>
          </div>

          <!-- Remove Button -->
          <div class="flex justify-end pt-1">
          </div>
        </div>
      }
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: [`
    /*
     * Entry highlight for a newly added field. Only a 'from' step is declared so the
     * animation resolves to the element's own computed background, which keeps the
     * effect correct under both the light (bg-white) and dark (bg-zinc-800) themes.
     */
    @keyframes field-appear {
      from { background-color: #fef9c3; } /* yellow-100 */
    }

    .field-appear {
      animation: field-appear 2s ease-out;
    }

    @media (prefers-reduced-motion: reduce) {
      .field-appear { animation: none; }
    }
  `]
})
export class TemplateFieldEditorComponent {
  @Input({ required: true }) fieldForm!: FormGroup;
  @Input({ required: true }) index!: number;
  @Input() isExpanded = false;
  @Input() fieldDefinition!: Field;

  @Output() onToggleExpansion = new EventEmitter<void>();
  @Output() editField = new EventEmitter<Field>();

  onEditFieldClick(event: Event): void {
    event.stopPropagation();
    this.editField.emit(this.fieldDefinition);
  }

  getPreviewText(): string {
    const val = this.fieldForm.get('exampleValue')?.value;
    if (val == null) return '';
    if (typeof val === 'object') {
      if (Array.isArray(val)) return `${val.length} item(s)`;
      return `{${Object.keys(val).length} field(s)}`;
    }
    return String(val);
  }
}
